// @ts-nocheck
import * as React from "react";
import * as V from "../api";
import { Badge, Button, Card, Field, Input, Modal, Select, TOKENS, Textarea, errorMessage, useToast } from "../ui";

// Phase 1 — 7국 + 2기둥 공개 메타. 로그인 사용자에게 "소속 신청" CTA.
// 돌봄연계국(C) 요약에 "통합돌봄" 백본 포지셔닝이 명시됨(e2e: bureaus-code-C).

const BUREAU_COLORS: Record<string, string> = {
  H: "var(--amber-500, #D9A24A)",
  E: "var(--green-500, #6FA88F)",
  L: "var(--sky-500, #5C82A8)",
  A: "var(--rose-500, #C9594F)",
  W: "var(--green-600, #4F8A72)",
  C: "var(--orange-500, #E8825B)", // 돌봄연계국 — 통합돌봄 브랜드 컬러
  P: "var(--amber-600, #B07F2E)",
  sponsor: TOKENS.ink3,
  endorse: TOKENS.ink2,
};

export function OverviewPanel({ bureaus, user }: { bureaus: V.Bureau[]; user: any | null }) {
  const [applying, setApplying] = React.useState<V.Bureau | null>(null);

  const grouped = React.useMemo(() => {
    const g = { bureau: [] as V.Bureau[], support: [] as V.Bureau[], endorse: [] as V.Bureau[] };
    bureaus.forEach((b) => g[b.kind]?.push(b));
    return g;
  }, [bureaus]);

  return (
    <div>
      <SectionTitle title="7개 교육국" subtitle="돌봄연계국(C)이 통합돌봄 시스템의 백본으로 결합됩니다." />
      <Grid>
        {grouped.bureau.map((b) => (
          <BureauCard key={b.id} bureau={b} canApply={!!user} onApply={() => setApplying(b)} />
        ))}
      </Grid>

      <SectionTitle title="2개 기둥" subtitle="자원 연결(마을지원국) + 공통 검증(지속가능발전협의회)" />
      <Grid>
        {[...grouped.support, ...grouped.endorse].map((b) => (
          <BureauCard key={b.id} bureau={b} canApply={!!user} onApply={() => setApplying(b)} />
        ))}
      </Grid>

      <ApplyModal
        open={!!applying}
        bureau={applying}
        onClose={() => setApplying(null)}
      />
    </div>
  );
}

function BureauCard({ bureau, canApply, onApply }: { bureau: V.Bureau; canApply: boolean; onApply: () => void }) {
  const color = BUREAU_COLORS[bureau.code] || TOKENS.ink3;
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: color, color: "#fff",
          display: "grid", placeItems: "center", fontWeight: 800, fontSize: 15,
        }}>{bureau.code === "sponsor" ? "지원" : bureau.code === "endorse" ? "협의" : bureau.code}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{bureau.name}</div>
          <Badge tone={bureau.kind === "bureau" ? "primary" : "neutral"}>
            {bureau.kind === "bureau" ? "교육국" : bureau.kind === "support" ? "지원 기둥" : "검증 기둥"}
          </Badge>
        </div>
      </div>
      <p style={{ fontSize: 12, lineHeight: 1.65, color: TOKENS.ink2, margin: "0 0 12px", minHeight: 48 }}>
        {bureau.summary}
      </p>
      {canApply && bureau.kind === "bureau" ? (
        <Button variant="secondary" size="sm" onClick={onApply}>이 국에 참여 신청</Button>
      ) : !canApply ? (
        <div style={{ fontSize: 11, color: TOKENS.ink3 }}>로그인하면 참여 신청이 가능합니다.</div>
      ) : null}
    </Card>
  );
}

function ApplyModal({ open, bureau, onClose }: { open: boolean; bureau: V.Bureau | null; onClose: () => void }) {
  const toast = useToast();
  const [role, setRole] = React.useState("village_parent");
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) { setRole("village_parent"); setNote(""); setSubmitting(false); }
  }, [open]);

  const submit = async () => {
    if (!bureau) return;
    setSubmitting(true);
    try {
      await V.members.apply({
        bureau_code: bureau.code,
        role_in_bureau: role,
        note: note || undefined,
      });
      toast.push("신청이 접수되었습니다. 운영자 승인 후 활성화됩니다.", "success");
      onClose();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={bureau ? `${bureau.name} 참여 신청` : ""}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "신청 중…" : "신청"}</Button>
        </>
      }
    >
      {bureau && (
        <>
          <p style={{ fontSize: 12, color: TOKENS.ink3, margin: "0 0 16px", lineHeight: 1.6 }}>
            {bureau.summary}
          </p>
          <Field label="활동 유형">
            <Select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="village_parent">학부모 네트워크</option>
              <option value="village_teacher">마을교사</option>
              <option value="village_youth">청소년</option>
              <option value="village_lead">리더(국장급)</option>
            </Select>
          </Field>
          <Field label="메모" hint="신청 사유나 활동 포부를 선택 입력">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="예: 매주 토요일 하천 조사에 참여하고 싶습니다." />
          </Field>
        </>
      )}
    </Modal>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: "grid", gap: 12, marginBottom: 28,
      gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    }}>{children}</div>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div style={{ margin: "4px 0 12px" }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: TOKENS.ink1, margin: 0 }}>{title}</h2>
      {subtitle && <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}
